import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Loader2, FileText, CheckCircle2, AlertCircle, ChevronLeft, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Ratings Migration Wizard.
 *
 * Three-step flow:
 *   1. Upload — user picks a PDF / Excel / CSV / TXT with legacy
 *      rider-note entries.
 *   2. Review — table of parsed entries with a per-row horse picker.
 *      Fuzzy-match confidence colour-codes each row:
 *        · green  "auto"       ≥80% match — safe as-is
 *        · amber  "suggested"  60-79%     — needs a look
 *        · red    "unmatched"  <60%       — pick manually or skip
 *      Every row has a "Skip" toggle so the user can drop nonsense
 *      LLM extractions in bulk.
 *   3. Commit — POST the confirmed rows. Each becomes a legacy
 *      chukka write-up (is_legacy=True). Numeric ranking is
 *      untouched.
 */
export default function RatingsMigrationPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null); // { job_id, status, progress, error, filename }
  const [parsed, setParsed] = useState(null); // final result payload
  const [rows, setRows] = useState([]);       // working copy with per-row edits
  const [horsesForSearch, setHorsesForSearch] = useState([]); // for override picker
  const [committing, setCommitting] = useState(false);

  const loadHorses = async () => {
    try {
      const res = await api.get("/horses/", { params: {} });
      setHorsesForSearch(res.data.map((h) => ({ id: h.id, name: h.name })).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (_e) { /* non-fatal — override picker just won't have options */ }
  };

  const applyParsedResult = async (data) => {
    setParsed(data);
    setRows((data.entries || []).map((e, idx) => ({
      idx,
      raw: e,
      skip: !(e.comment && e.comment.trim().length > 0),
      chosen_horse_id: e.match?.horse_id || null,
      rider_name: e.rider_name || "",
      timestamp: e.timestamp || "",
      comment: e.comment || "",
    })));
    await loadHorses();
    if (data.truncated) {
      toast.warning("File was very large — showing the first 500 entries. Split the file if you need more.");
    } else {
      toast.success(`Parsed ${data.entries?.length || 0} entries. Review below.`);
    }
  };

  // Poll the /status endpoint every 2 seconds while a job is running.
  // On completion we hydrate `parsed` + `rows` and clear the job so
  // the progress UI collapses. On error we surface the message via
  // toast and reset so the user can retry.
  useEffect(() => {
    if (!job || (job.status !== "queued" && job.status !== "processing")) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get(`/ratings/migrate/status/${job.job_id}`);
        if (cancelled) return;
        const s = res.data;
        setJob((prev) => ({ ...prev, ...s }));
        if (s.status === "done" && s.result) {
          await applyParsedResult(s.result);
          setJob(null);
          setUploading(false);
        } else if (s.status === "error") {
          toast.error(s.error || "Parse failed. Please try again.");
          setJob(null);
          setUploading(false);
        }
      } catch (e) {
        if (cancelled) return;
        // Transient errors — keep polling. Job doc TTL is server-side.
      }
    };
    const iv = setInterval(tick, 2000);
    // Fire immediately too so the first update lands sub-second.
    tick();
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, job?.status]);

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    setParsed(null);
    setRows([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/ratings/migrate/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Response is now immediate — we get a job_id and poll for the result.
      setJob({
        job_id: res.data.job_id,
        status: res.data.status || "queued",
        progress: null,
        filename: file.name,
      });
      toast.info("Parsing… this can take up to a minute for large files.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the parse job");
      setUploading(false);
    }
  };

  const setRow = (idx, patch) => {
    setRows((prev) => prev.map((r) => (r.idx === idx ? { ...r, ...patch } : r)));
  };

  const stats = useMemo(() => {
    const eligible = rows.filter((r) => !r.skip && r.chosen_horse_id && r.comment.trim());
    const skipped = rows.filter((r) => r.skip);
    const needsHorse = rows.filter((r) => !r.skip && !r.chosen_horse_id);
    return { eligible: eligible.length, skipped: skipped.length, needsHorse: needsHorse.length, total: rows.length };
  }, [rows]);

  const commit = async () => {
    const payload = rows
      .filter((r) => !r.skip && r.chosen_horse_id && r.comment.trim())
      .map((r) => ({
        horse_id: r.chosen_horse_id,
        rider_name: r.rider_name?.trim() || null,
        timestamp: r.timestamp?.trim() || null,
        comment: r.comment.trim(),
        competition_type: "Legacy import",
      }));
    if (payload.length === 0) {
      toast.error("Nothing to import — every eligible row is either skipped or missing a horse.");
      return;
    }
    setCommitting(true);
    try {
      const res = await api.post("/ratings/migrate/commit", { entries: payload });
      toast.success(`Imported ${res.data.created} legacy rating${res.data.created === 1 ? "" : "s"}.`);
      // Redirect back to horses inventory so operator can see the ratings
      // reflected on the relevant horse detail pages.
      navigate("/horses");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ratings-migration-page">
      <PageHeader
        title="Import legacy ratings"
        subtitle="Upload a PDF, Excel or CSV of historical rider notes. We'll extract them, fuzzy-match your existing horses, and you confirm before anything is saved."
        actions={
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="ratings-migration-back">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        }
      />

      {/* Step 1 — Upload */}
      <Card className="p-4 md:p-6" data-testid="ratings-migration-upload-card">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1 min-w-0">
            <label className="text-sm font-medium flex items-center gap-2 mb-1.5">
              <FileText className="h-4 w-4 text-muted-foreground" />
              File (.pdf, .xlsx, .xls, .csv, .tsv, .txt — max 10 MB)
            </label>
            <Input
              type="file"
              accept=".pdf,.xlsx,.xls,.csv,.tsv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,text/tab-separated-values,text/plain"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="ratings-migration-file-input"
              className="cursor-pointer"
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Legacy notes usually only need a timestamp, rider name and comment. Extra columns are fine — we'll ignore what we can't use.
            </p>
          </div>
          <Button
            onClick={onUpload}
            disabled={!file || uploading || !!job}
            data-testid="ratings-migration-parse-button"
          >
            {uploading || job
              ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Parsing…</>
              : <><Wand2 className="h-4 w-4 mr-2" /> Parse file</>}
          </Button>
        </div>
      </Card>

      {/* Progress card — visible while a parse job is running. Shows a
          chunked progress bar so the user knows the request didn't
          just stall; large files can take up to a minute. */}
      {job && (
        <Card className="p-4 md:p-6" data-testid="ratings-migration-job-card">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--ap-forest))]" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {job.status === "queued" ? "Queued…" : "Parsing your file…"}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {job.filename || "Uploaded file"}
              </div>
            </div>
            {job.progress && job.progress.chunks_total > 0 && (
              <span
                className="text-xs font-mono tabular-nums text-muted-foreground"
                data-testid="ratings-migration-job-progress-text"
              >
                {job.progress.chunks_done}/{job.progress.chunks_total} chunks
                {" · "}
                {job.progress.entries_so_far} entries so far
              </span>
            )}
          </div>
          <div
            className="w-full h-2 rounded-full bg-muted overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={
              job.progress && job.progress.chunks_total > 0
                ? Math.round((job.progress.chunks_done / job.progress.chunks_total) * 100)
                : 0
            }
            data-testid="ratings-migration-job-progress-bar"
          >
            <div
              className="h-full bg-[hsl(var(--ap-forest))] transition-[width] duration-500 ease-out"
              style={{
                width: job.progress && job.progress.chunks_total > 0
                  ? `${Math.max(6, Math.round((job.progress.chunks_done / job.progress.chunks_total) * 100))}%`
                  : "6%",
              }}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            You can leave this tab open — parsing continues in the background even if the request would have timed out.
          </p>
        </Card>
      )}

      {/* Step 2 — Review */}
      {parsed && (
        <Card className="p-4 md:p-6" data-testid="ratings-migration-review-card">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="text-sm text-muted-foreground flex-1 min-w-0">
              <span className="font-medium text-foreground">{parsed.source_filename || "Uploaded file"}</span>
              {" · "}{parsed.entries?.length || 0} entries parsed
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--ap-forest)/0.10)] text-[hsl(var(--ap-forest))] px-2.5 py-1 text-xs font-medium">
              <CheckCircle2 className="h-3 w-3" /> Ready: {stats.eligible}
            </span>
            {stats.needsHorse > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] px-2.5 py-1 text-xs font-medium">
                <AlertCircle className="h-3 w-3" /> Need horse: {stats.needsHorse}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/40 text-muted-foreground px-2.5 py-1 text-xs font-medium">
              Skipped: {stats.skipped}
            </span>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No entries were extracted. That usually means the file was scanned (OCR needed) or empty. Try re-saving as CSV or Excel and upload again.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border" data-testid="ratings-migration-review-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2.5 w-10">Skip</th>
                    <th className="text-left px-3 py-2.5">Horse (from file → matched)</th>
                    <th className="text-left px-3 py-2.5">Timestamp</th>
                    <th className="text-left px-3 py-2.5">Rider</th>
                    <th className="text-left px-3 py-2.5 min-w-[320px]">Comment / write-up</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <MigrationRow
                      key={r.idx}
                      row={r}
                      horses={horsesForSearch}
                      onChange={(patch) => setRow(r.idx, patch)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Commit button */}
          {rows.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
              <p className="text-xs text-muted-foreground flex-1">
                Legacy entries are stored as rider notes only — they never affect the numeric ranking score.
              </p>
              <ConfirmDialog
                title={`Import ${stats.eligible} legacy rating${stats.eligible === 1 ? "" : "s"}?`}
                description={
                  <>
                    <p>{stats.eligible} entries will be added as historical rider notes on the matched horses. They'll show up under "Rider notes" in the rankings expanded view and on each horse's detail page.</p>
                    {stats.needsHorse > 0 && (
                      <p className="mt-2 text-xs">{stats.needsHorse} row{stats.needsHorse === 1 ? "" : "s"} still {stats.needsHorse === 1 ? "does" : "do"} not have a horse selected and will be excluded.</p>
                    )}
                    <p className="mt-2 text-xs">Numeric rankings are unaffected — legacy notes never influence scores.</p>
                  </>
                }
                confirmLabel="Yes, import"
                destructive={false}
                onConfirm={commit}
                testId="ratings-migration-commit"
              >
                <Button disabled={committing || stats.eligible === 0} data-testid="ratings-migration-commit-button">
                  {committing
                    ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    : <Upload className="h-4 w-4 mr-2" />}
                  Import {stats.eligible} rating{stats.eligible === 1 ? "" : "s"}
                </Button>
              </ConfirmDialog>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * One row in the review table. Kept as its own component so that
 * editing a single row doesn't force a full-table re-render of 200
 * inputs, which was noticeably janky on larger imports during
 * testing.
 */
function MigrationRow({ row, horses, onChange }) {
  const raw = row.raw;
  const match = raw?.match || {};
  // Confidence badge colour ties to backend-computed status so this
  // stays consistent with the parse response.
  const badge =
    match.status === "auto"
      ? { text: `Match ${match.confidence}%`, cls: "bg-[hsl(var(--ap-forest)/0.10)] text-[hsl(var(--ap-forest))] border-[hsl(var(--ap-forest)/0.3)]" }
      : match.status === "suggested"
        ? { text: `Suggested ${match.confidence}%`, cls: "bg-[hsl(var(--ap-brass)/0.10)] text-[hsl(var(--ap-saddle))] border-[hsl(var(--ap-brass)/0.35)]" }
        : { text: "No match", cls: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.35)]" };
  return (
    <tr className={`border-t border-border ${row.skip ? "opacity-50" : ""}`} data-testid={`migration-row-${row.idx}`}>
      <td className="px-3 py-2.5 align-top">
        <Checkbox
          checked={row.skip}
          onCheckedChange={(v) => onChange({ skip: !!v })}
          aria-label="Skip this row"
          data-testid={`migration-row-skip-${row.idx}`}
        />
      </td>
      <td className="px-3 py-2.5 align-top min-w-[220px]">
        <div className="text-xs text-muted-foreground mb-1">Original: <span className="font-medium text-foreground">{raw.horse_name || <em>—</em>}</span></div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${badge.cls}`}>
            {badge.text}
          </span>
        </div>
        <div className="mt-1.5">
          <Select
            value={row.chosen_horse_id || "__none"}
            onValueChange={(v) => onChange({ chosen_horse_id: v === "__none" ? null : v })}
          >
            <SelectTrigger className="h-8 text-xs" data-testid={`migration-row-horse-${row.idx}`}>
              <SelectValue placeholder="Pick a horse…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— None (will be skipped) —</SelectItem>
              {horses.map((h) => (
                <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </td>
      <td className="px-3 py-2.5 align-top w-[180px]">
        <Input
          type="text"
          value={row.timestamp}
          onChange={(e) => onChange({ timestamp: e.target.value })}
          className="h-8 text-xs"
          placeholder="ISO or free text"
          data-testid={`migration-row-timestamp-${row.idx}`}
        />
      </td>
      <td className="px-3 py-2.5 align-top w-[160px]">
        <Input
          type="text"
          value={row.rider_name}
          onChange={(e) => onChange({ rider_name: e.target.value })}
          className="h-8 text-xs"
          placeholder="Rider name"
          data-testid={`migration-row-rider-${row.idx}`}
        />
      </td>
      <td className="px-3 py-2.5 align-top">
        <Textarea
          value={row.comment}
          onChange={(e) => onChange({ comment: e.target.value })}
          rows={2}
          className="text-xs resize-y"
          placeholder="Comment / write-up"
          data-testid={`migration-row-comment-${row.idx}`}
        />
        {Number.isFinite(raw.score) && (
          <div className="mt-1 text-[10px] text-muted-foreground">Parsed score: {raw.score} (not imported — legacy notes don't affect ranking)</div>
        )}
      </td>
    </tr>
  );
}
